import config from '@/config'
import { createAuthenticatedFetch } from '@/helpers/api/httpClient'
import type {
	AdminIssueLicenseRequest,
	LicenseStatusTypes,
	PaginationResponse,
	SearchTenantLicensesRequest,
	TenantLicenseDetailResponse,
	TenantLicenseListResponse,
	UpdateTrialLicenseLimitsRequest,
} from './licenseTypes'

const apiFetch = createAuthenticatedFetch()
const baseUrl = () => `${config.API_URL}/api/subscription`

async function parseJson<T>(response: Response): Promise<T> {
	if (!response.ok) {
		const err = await response.json().catch(() => ({}))
		return Promise.reject(err)
	}
	return response.json() as Promise<T>
}

export const licenseAdminApi = {
	search: async (request: SearchTenantLicensesRequest) => {
		const res = await apiFetch(`${baseUrl()}/licenses/search`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(request),
		})
		return parseJson<PaginationResponse<TenantLicenseListResponse>>(res)
	},

	getById: async (id: number) => {
		const res = await apiFetch(`${baseUrl()}/licenses/${id}`)
		return parseJson<TenantLicenseDetailResponse>(res)
	},

	generate: async (tenantSubscriptionPlanId: number) => {
		const res = await apiFetch(`${baseUrl()}/licenses/subscription/${tenantSubscriptionPlanId}/generate`, {
			method: 'POST',
		})
		return parseJson<TenantLicenseDetailResponse>(res)
	},

	issue: async (body: AdminIssueLicenseRequest) => {
		const res = await apiFetch(`${baseUrl()}/licenses/issue`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(body),
		})
		return parseJson<TenantLicenseDetailResponse>(res)
	},

	updateStatus: async (id: number, licenseStatus: LicenseStatusTypes, reason?: string) => {
		const res = await apiFetch(`${baseUrl()}/licenses/${id}/status`, {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ licenseStatus, reason }),
		})
		return parseJson<string>(res)
	},

	updateTrialLimits: async (id: number, body: UpdateTrialLicenseLimitsRequest) => {
		const res = await apiFetch(`${baseUrl()}/licenses/${id}/trial-limits`, {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(body),
		})
		return parseJson<TenantLicenseDetailResponse>(res)
	},

	resendEmail: async (id: number) => {
		const res = await apiFetch(`${baseUrl()}/licenses/${id}/resend-email`, { method: 'POST' })
		return parseJson<string>(res)
	},

	getAnalyticsSummary: async () => {
		const res = await apiFetch(`${baseUrl()}/licenses/analytics/summary`)
		return parseJson<LicenseAnalyticsSummaryResponse>(res)
	},

	searchAudit: async (request: SearchLicenseAuditRequest) => {
		const res = await apiFetch(`${baseUrl()}/licenses/audit/search`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(request),
		})
		return parseJson<PaginationResponse<LicenseAuditLogResponse>>(res)
	},
}

export interface LicenseAnalyticsSummaryResponse {
	totalLicenses: number
	activeLicenses: number
	trialLicenses: number
	paidLicenses: number
	pendingIssue: number
	totalActiveDevices: number
	expiringWithin7Days: number
	estimatedMrr: number
}

export interface LicenseAuditLogResponse {
	id: number
	licenseId?: number
	tenantId?: number
	eventType: string
	actorUserId?: string
	summary?: string
	createdOn: string
}

export interface SearchLicenseAuditRequest {
	pageNumber: number
	pageSize: number
	eventType?: string
	tenantId?: number
	licenseId?: number
	keyword?: string
	includeRoutineEvents?: boolean
}
